import { useState, useRef, useEffect } from 'react';

interface UseSwipeNavigationProps {
  initialPage?: number;
  totalPages: number;
}

interface UseSwipeNavigationReturn {
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  swipeContainerRef: React.RefObject<HTMLDivElement>;
  tabRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
}

export function useSwipeNavigation({ initialPage = 0, totalPages }: UseSwipeNavigationProps): UseSwipeNavigationReturn {
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const isSwiping = useRef<boolean>(false);
  const currentPageRef = useRef<number>(currentPage);
  const swipeContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // Single set of native touch event listeners (no React handlers to avoid duplicates)
  useEffect(() => {
    const container = swipeContainerRef.current;
    if (!container) return;

    const handleTouchStartNative = (e: globalThis.TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchEndX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      isSwiping.current = false;
    };

    const handleTouchMoveNative = (e: globalThis.TouchEvent) => {
      const diffX = Math.abs(e.touches[0].clientX - touchStartX.current);
      const diffY = Math.abs(e.touches[0].clientY - touchStartY.current);

      // Only count as horizontal swipe if X movement > Y movement (avoid interfering with scrolling)
      if (!isSwiping.current && diffX > 10 && diffX > diffY) {
        isSwiping.current = true;
      }
      touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEndNative = () => {
      if (!isSwiping.current) return;

      const diff = touchStartX.current - touchEndX.current;
      const threshold = 50;
      const page = currentPageRef.current;

      if (Math.abs(diff) > threshold) {
        if (diff > threshold) {
          setCurrentPage((page + 1) % totalPages);
        } else if (diff < -threshold) {
          setCurrentPage((page - 1 + totalPages) % totalPages);
        }
      }

      isSwiping.current = false;
    };

    container.addEventListener('touchstart', handleTouchStartNative, { passive: true });
    container.addEventListener('touchmove', handleTouchMoveNative, { passive: true });
    container.addEventListener('touchend', handleTouchEndNative, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStartNative);
      container.removeEventListener('touchmove', handleTouchMoveNative);
      container.removeEventListener('touchend', handleTouchEndNative);
    };
  }, []); // Empty deps - use ref for currentPage to avoid re-registering

  // Auto-scroll active tab into view
  useEffect(() => {
    const activeTab = tabRefs.current[currentPage];
    if (activeTab) {
      activeTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [currentPage, totalPages]);

  return {
    currentPage,
    setCurrentPage,
    swipeContainerRef,
    tabRefs,
  };
}
